import {
  extractPlaceholderTicketsFromMarkdown,
  replacePlaceholderInMarkdown,
} from '@/components/editor/extractPlaceholderTicketsFromMarkdown';
import { createTicketEvent, TicketEvent } from '@certhon/models';

export interface ProcessPlaceholderResult {
  markdown: string;
  ticketEvents: TicketEvent[];
}

/**
 * Process placeholder tickets in markdown by preparing ticket creation events.
 *
 * This function:
 * 1. Extracts all #'Title' placeholders from markdown
 * 2. Generates ticket IDs and creates ticket events for each placeholder
 * 3. Replaces placeholders with actual ticket ID references (#ticket-id+)
 * 4. Returns both the updated markdown and the events to be posted atomically
 *
 * The caller should post all events (including the returned ticketEvents) in a single
 * atomic operation to ensure consistency.
 *
 * @param markdown - The markdown string containing placeholders
 * @param context - Context for creating tickets (userId, projectId, werkId, originTicketId)
 * @returns Object containing updated markdown and array of CREATE_TICKET events
 */
export function processPlaceholderTickets(
  markdown: string,
  context: {
    userId: number;
    projectId?: number | null;
    werkId?: number | null;
    /**
     * klant of the referenced project/werk — CRM-chain contract: placeholder
     * tickets inherit the origin ticket's full (already resolved) chain.
     */
    clientId?: number | null;
    originTicketId: string;
  },
): ProcessPlaceholderResult {
  // Step 1: Extract placeholder tickets from markdown
  const placeholderTickets = extractPlaceholderTicketsFromMarkdown(markdown);

  // If no placeholders, return original markdown with no events
  if (placeholderTickets.length === 0) {
    return { markdown, ticketEvents: [] };
  }

  // Step 2: Create ticket events and ID mapping for each placeholder
  const ticketEvents: TicketEvent[] = [];
  let updatedMarkdown = markdown;

  placeholderTickets.forEach((placeholder) => {
    const newTicketId = crypto.randomUUID();

    // Create ticket event
    ticketEvents.push(
      createTicketEvent('CREATE_TICKET', {
        user_id: context.userId,
        ticket_id: newTicketId,
        payload: {
          title: placeholder.title,
          status: 'opened' as const,
          // Same default as the create form: whoever spawns the ticket owns it
          assignee_user_id: context.userId,
          // Explicit chain — `?? null` keeps the keys present when the
          // context field is undefined (chain completeness is key presence)
          project_id: context.projectId ?? null,
          werk_id: context.werkId ?? null,
          client_id: context.clientId ?? null,
        },
        meta: {
          originTicketId: context.originTicketId,
        },
      }),
    );

    // Replace placeholder with ticket ID reference (with + for showTitle)
    // The backend will assign a ticket number, but we use the ID for now
    updatedMarkdown = replacePlaceholderInMarkdown(
      updatedMarkdown,
      placeholder.originalText,
      newTicketId,
    );
  });

  return { markdown: updatedMarkdown, ticketEvents };
}
